import React, { Fragment, useContext } from "react";
import { Listbox as HeadlessListBox, ListboxButtonProps } from "@headlessui/react";
import { useColorSchemeFn } from "@ugitgud/legos/hooks/useColorScheme";
import { WithColorScheme, WithClassName, BaseOptionValueType, WithAsChildConditionalProps } from "@ugitgud/legos/ui/shared";
import { DropdownColorSchemes, DropdownOptionType } from "@ugitgud/legos/ui/form/dropdown/types";
import { useMergeRefs } from "@floating-ui/react";
import { DropdownContext } from "@ugitgud/legos/ui/form/dropdown/Dropdown";

type AsFragmentProps = Omit<ListboxButtonProps<typeof Fragment>, "children" | "className" | "as">;
type AsChildProps = Omit<ListboxButtonProps<"button">, "children" | "className" | "as">;

export type DropdownButtonProps<T extends DropdownOptionType<BaseOptionValueType>> = WithAsChildConditionalProps<
  AsFragmentProps,
  AsChildProps
> &
  WithClassName<WithColorScheme<{}, DropdownColorSchemes>> & {
    children?: DropdownButtonChildren<T>;
  };

export type DropdownButtonRenderPropArgs<T extends DropdownOptionType<BaseOptionValueType>> = {
  open: boolean;
  disabled: boolean;
  value: T | undefined;
};

export type DropdownButtonChildren<T extends DropdownOptionType<BaseOptionValueType>> =
  | React.ReactNode
  | ((props: DropdownButtonRenderPropArgs<T>) => React.ReactElement<any, string | React.JSXElementConstructor<any>>);

export const DropdownButton = React.forwardRef(function <T extends DropdownOptionType<BaseOptionValueType>>(
  { className, colorScheme, asChild, ...rest }: DropdownButtonProps<T>,
  ref: React.ForwardedRef<HTMLElement>,
) {
  const scheme = useColorSchemeFn({
    classes: {
      secondary: "dark:bg-purple-100 dark:text-lavender-50 w-full h-full",
    },
    default: "secondary",
    className: className,
    scheme: colorScheme,
  });

  const as = asChild ? Fragment : "button";
  const classNameProp = asChild ? {} : { className: scheme };

  const { setReference, hasAnchorProp } = useContext(DropdownContext);
  const mergedRefs = useMergeRefs([ref, !hasAnchorProp ? setReference : undefined]);

  return <HeadlessListBox.Button ref={mergedRefs} {...rest} as={as} {...classNameProp} />;
});
